import { useMemo, useState } from 'react';
import { isFunction } from 'lodash/fp';
import { useDefaultId } from 'app/lib/hooks';
import {
    DialogWithChapters,
    DialogContext,
    DialogConfigurePanel,
    DialogSummary,
    DialogSidebar,
    DialogFormContent
} from 'app/components/features/DialogWithChapters';
import { ErrorBoundary } from 'app/components/plumbing/ErrorBoundary';
import { ConfigureServiceAccountPanel } from './ConfigureServiceAccountPanel.jsx';
import { ConfigureEnvironmentPanel } from './ConfigureEnvironmentPanel.jsx';

export function JobDialog({ children, title, error, onClose, definitionUid }) {
    const accId = useDefaultId('panel-acc');
    const envId = useDefaultId('panel-env');
    const jobId = useDefaultId('panel-job');

    const [accSummary, setAccSummary] = useState(null);
    const [envSummary, setEnvSummary] = useState(null);
    const [jobSummary, setJobSummary] = useState({});

    const [accDetails, setAccDetails] = useState(null);
    const [envDetails, setEnvDetails] = useState(null);

    function clearAccountDetails() {
        setAccSummary(null);
        setAccDetails(null);
    }

    function clearEnvironmentDetails() {
        setEnvSummary(null);
        setEnvDetails(null);
    }

    const context = useMemo(() => {
        function handleAccountUpdated({ uid }) {
            const { onCreate } = accDetails;
            if (isFunction(onCreate)) {
                onCreate(uid);
            }
            clearAccountDetails();
        }

        function handleEnvironmentUpdated({ uid }) {
            const { onCreate } = envDetails;
            if (isFunction(onCreate)) {
                onCreate(uid);
            }
            clearEnvironmentDetails();
        }

        return {
            onEditAccount: setAccDetails,
            onUpdateAccount: handleAccountUpdated,
            onUpdateAccountSummary: setAccSummary,
            onEditEnvironment: setEnvDetails,
            onUpdateEnvironment: handleEnvironmentUpdated,
            onUpdateEnvironmentSummary: setEnvSummary,
            onUpdateJobSummary: setJobSummary
        };
    }, [accDetails, envDetails]);

    return (
        <DialogWithChapters
            title={title}
            error={error}
            onClose={onClose}
            id="job-definition-dialog">
            <DialogContext.Provider value={context}>
                <DialogFormContent>
                    {accDetails && (
                        <ConfigureServiceAccountPanel
                            id={accId}
                            account={accDetails.account}
                            onCancel={clearAccountDetails}
                        />
                    )}
                    {envDetails && (
                        <ConfigureEnvironmentPanel
                            id={envId}
                            open={!accDetails}
                            environment={envDetails.environment}
                            onCancel={clearEnvironmentDetails}
                        />
                    )}
                    <DialogConfigurePanel
                        id={jobId}
                        open={!envDetails}
                        definitionUid={definitionUid}>
                        <ErrorBoundary prefix="Unhandled Error in Job Dialog">
                            {children}
                        </ErrorBoundary>
                    </DialogConfigurePanel>
                </DialogFormContent>
                <DialogSidebar>
                    {accSummary && <DialogSummary formId={accId} summary={accSummary} />}
                    {envSummary && (
                        <DialogSummary formId={envId} summary={envSummary} open={!accSummary} />
                    )}
                    <DialogSummary formId={jobId} summary={jobSummary} open={true} />
                </DialogSidebar>
            </DialogContext.Provider>
        </DialogWithChapters>
    );
}
